import { QueryClient } from '@tanstack/react-query'

import { getChain } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  RelayNetworkMetadata,
  useRelayNetworkInfo,
} from '~/hooks/useRelayNetworks'

type RelayNetworkInfo = ReturnType<typeof useRelayNetworkInfo>

/**
 * Get the display label for a chain
 * @param chainId - The chain ID to get the label for
 * @param options - Optional configuration
 * @param options.relayNetwork - Pre-fetched relay network info
 * @param options.queryClient - React Query client to check cache for relay network (for non-hook contexts)
 * @returns The chain label (from the chain registry, relay network, or fallback)
 */
export const getChainLabel = (
  chainId: number,
  options?: {
    relayNetwork?: RelayNetworkInfo
    queryClient?: QueryClient
  },
): string => {
  const chain = getChain(chainId)
  if (chain) {
    return chain.name
  }

  // If relay network info was provided, use it
  if (options?.relayNetwork) {
    return options.relayNetwork.displayName
  }

  // If queryClient was provided, check cache for relay network
  if (options?.queryClient) {
    const relayNetwork = options.queryClient.getQueryData<RelayNetworkMetadata>(
      [QUERY_KEYS.RELAY_NETWORK, chainId],
    )
    if (relayNetwork) {
      return relayNetwork.displayName
    }
  }

  // Fallback
  return `Chain ${chainId}`
}

/**
 * Hook to get the display label for a chain
 * Automatically fetches relay network info if needed
 * @param chainId - The chain ID to get the label for
 * @returns The chain label
 */
export const useChainLabel = (chainId: number): string => {
  const relayNetwork = useRelayNetworkInfo(chainId)
  return getChainLabel(chainId, { relayNetwork })
}

export function properlyFormatOneOrMoreChainNames(chainNames: string[]) {
  switch (chainNames.length) {
    case 0:
      return 'no networks'
    case 1:
      return chainNames[0]
    case 2:
      return `${chainNames[0]} and ${chainNames[1]}`
    default:
      return `${chainNames.slice(0, -1).join(', ')}, and ${
        chainNames.slice(-1)[0]
      }`
  }
}
